import { toastOnError } from '@src/@data/utility/errors';
import { useMessages } from '@src/@data/utility/hooks/useMessages';
import { validatePassword } from '@src/@data/utility/password';
import { toastOnSuccess } from '@src/@data/utility/toasts';
import { useCallback, useMemo, useState } from 'react';
import {
  Button,
  Form,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from 'reactstrap';
import { useAuth } from '../../../@data/store/AuthProvider';
import { CreatePasswordFormGroup } from './CreatePasswordFormGroup';
import { PasswordInput } from './PasswordInput';

interface ChangeUserPasswordModalProps {
  toggle: () => void;
}

export const ChangeUserPasswordModal: React.FC<ChangeUserPasswordModalProps> =
  ({ toggle }) => {
    const { messages } = useMessages();
    const { changePassword } = useAuth();
    const [currentPassword, setCurrentPassword] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isValid = useMemo(
      () =>
        !!currentPassword &&
        validatePassword(password) &&
        password === confirmPassword,
      [confirmPassword, currentPassword, password]
    );

    const submit = useCallback(async () => {
      setIsSubmitting(true);
      changePassword({ current: currentPassword, password })
        .then(() => {
          toastOnSuccess(messages('change_password_modal_success'));
          setIsSubmitting(false);
          toggle();
        })
        .catch((err) => {
          setIsSubmitting(false);
          toastOnError(err);
        });
    }, [changePassword, currentPassword, messages, password, toggle]);

    return (
      <Form onSubmit={(e) => e.preventDefault()}>
        <Modal isOpen centered>
          <ModalHeader toggle={toggle}>
            {messages('nav_user_menu_change_password')}
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label for="current-password">
                {messages('current_password_label')}
              </Label>
              <PasswordInput
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCurrentPassword(e.target.value)
                }
                autoComplete="current-password"
              />
            </FormGroup>
            <CreatePasswordFormGroup
              password={password}
              setPassword={setPassword}
              setConfirmPassword={setConfirmPassword}
              confirmPassword={confirmPassword}
              showLabels
            />
          </ModalBody>
          <ModalFooter>
            <Button type="button" color="secondary" onClick={toggle}>
              {messages('action_cancel')}
            </Button>
            <Button
              type="submit"
              color="primary"
              onClick={submit}
              disabled={!isValid || isSubmitting}
            >
              <div className="d-flex align-items-center">
                {messages('dialog_action_save')}
                {isSubmitting && <Spinner size="sm" className="ml-50" />}
              </div>
            </Button>
          </ModalFooter>
        </Modal>
      </Form>
    );
  };
